/* LBRGT Highlights - pagination dots for the image tile slider.
   Progressive enhancement: without this file the track stays an ordinary scroll-snap strip,
   so there are never dead buttons. The dots only exist when the track really overflows,
   which is why they are built here and not in Liquid - the page count depends on the
   viewport, not on the number of blocks.
   The dots container carries role/aria-label only while it holds buttons; both are removed
   on teardown so an empty container never announces a group that is not there. */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768.98px)';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var observers = new WeakMap();
  var frames = new WeakMap();

  /* 2px tolerance: scrollWidth and clientWidth are rounded, and a fractional tile width
     (243.2) otherwise reports a phantom second page. */
  function pageCount(track) {
    if (!track.clientWidth) return 0;
    return Math.ceil((track.scrollWidth - 2) / track.clientWidth);
  }

  function maxScroll(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  function teardown(dots) {
    dots.textContent = '';
    dots.removeAttribute('role');
    dots.removeAttribute('aria-label');
    delete dots.dataset.lbrgtPages;
    delete dots.dataset.lbrgtReady;
  }

  function setActive(dots, track) {
    var buttons = dots.children;
    if (!buttons.length || !track.clientWidth) return;
    // RTL scrolls into negative numbers in some engines
    var left = Math.abs(track.scrollLeft);
    var index;
    /* The last page is shorter than one viewport, so (pages - 1) * clientWidth is usually
       out of reach and a plain round() would never land on the last dot. Being at the end
       of the track IS the last page - 1px of slack for fractional widths. */
    if (maxScroll(track) - left <= 1) {
      index = buttons.length - 1;
    } else {
      index = Math.round(left / track.clientWidth);
      if (index > buttons.length - 1) index = buttons.length - 1;
    }
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-current', i === index ? 'true' : 'false');
    }
  }

  function scrollToPage(track, index) {
    // clamped, so the last dot asks for a position the track can actually reach
    var target = Math.min(index * track.clientWidth, maxScroll(track));
    if (window.getComputedStyle(track).direction === 'rtl') target = -target;
    track.scrollTo({
      left: target,
      behavior: reduceMotion.matches ? 'auto' : 'smooth'
    });
  }

  function build(dots) {
    var track = document.getElementById(dots.getAttribute('data-track'));
    if (!track) return;

    var pages = pageCount(track);
    if (pages < 2) {
      if (dots.dataset.lbrgtReady) teardown(dots);
      return;
    }
    // idempotent: the editor re-runs this on every section reload and every resize tick
    if (dots.dataset.lbrgtReady === 'true' && dots.dataset.lbrgtPages === String(pages)) {
      setActive(dots, track);
      return;
    }

    teardown(dots);

    for (var i = 0; i < pages; i++) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'lbrgt-hl__dot';
      button.setAttribute('aria-label', 'Seite ' + (i + 1) + ' von ' + pages);
      button.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      button.addEventListener('click', (function (index) {
        return function () {
          scrollToPage(track, index);
        };
      })(i));
      dots.appendChild(button);
    }

    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', dots.getAttribute('data-dots-label') || 'Seitennavigation');
    dots.dataset.lbrgtPages = String(pages);
    dots.dataset.lbrgtReady = 'true';

    setActive(dots, track);

    /* Bound once per track. The container element survives every rebuild - only its
       children are replaced - so the closure stays valid and no listener piles up. */
    if (track.dataset.lbrgtHlBound !== 'true') {
      track.dataset.lbrgtHlBound = 'true';
      track.addEventListener(
        'scroll',
        function () {
          if (frames.get(track)) return;
          frames.set(
            track,
            window.requestAnimationFrame(function () {
              frames.delete(track);
              setActive(dots, track);
            })
          );
        },
        { passive: true }
      );
    }

    if (window.ResizeObserver && !observers.has(dots)) {
      var observer = new ResizeObserver(function () {
        build(dots);
      });
      observer.observe(track);
      observers.set(dots, observer);
    }
  }

  function sync(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var list = context.querySelectorAll('[data-lbrgt-hl-dots]');
    Array.prototype.forEach.call(list, build);
    // a section:load event hands us the section wrapper itself, not an ancestor
    if (context !== document && context.matches && context.matches('[data-lbrgt-hl-dots]')) {
      build(context);
    }
  }

  function init() {
    sync(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // the viewport decides how many tiles fit, so the page count changes with the breakpoint
  var mobile = window.matchMedia(MOBILE_QUERY);
  var onViewportChange = function () {
    sync(document);
  };
  if (mobile.addEventListener) mobile.addEventListener('change', onViewportChange);
  else if (mobile.addListener) mobile.addListener(onViewportChange);

  // theme editor: reloading or selecting the section must not duplicate the dot row
  document.addEventListener('shopify:section:load', function (event) {
    sync(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var list = event.target.querySelectorAll
      ? event.target.querySelectorAll('[data-lbrgt-hl-dots]')
      : [];
    Array.prototype.forEach.call(list, function (dots) {
      var observer = observers.get(dots);
      if (observer) {
        observer.disconnect();
        observers.delete(dots);
      }
      teardown(dots);
    });
  });
  document.addEventListener('shopify:block:select', function () {
    sync(document);
  });
})();
